import React from 'react';

import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Chip, Container, Stack, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { getBusiness } from '~/services/BusinessTraveller/BusinessTravellerService';

import BusinessDetailsHeaderActions from './BusinessDetailsHeaderActions';
import { tabsMap } from './businessTabs';

interface BusinessTabContentProps {
  businessId: string;
  tab: string;
  visibleTabs: Array<string>;
}

export default function BusinessTabContent({ businessId, tab, visibleTabs }: BusinessTabContentProps) {
  const currentTab = tabsMap[tab];

  const { data: business } = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => getBusiness(businessId),
    enabled: !!businessId,
  });

  const isDeactivated = !!business?.deletedAt;
  const title = `Business / ${currentTab.label}`;

  return (
    <Container>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <PageHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <span>{title}</span>
            {isDeactivated && <Chip label="Deactivated" size="small" color="error" variant="outlined" />}
          </Stack>
        }
      >
        <BusinessDetailsHeaderActions businessId={businessId} />
      </PageHeader>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab}>
          {visibleTabs.map((tab) => (
            <Tab
              component={Link}
              key={tab}
              label={tabsMap[tab].label}
              to={`/lebusiness/${businessId}/${tab}`}
              value={tab}
            />
          ))}
        </Tabs>
      </Box>

      <Box mt={4}>
        {isDeactivated && (
          <Alert severity="error" sx={{ mb: 2, width: 'fit-content' }}>
            This business has been deactivated. Records have been deactivated and no accounts can sign in.
          </Alert>
        )}
        <Box component={currentTab.component} />
      </Box>
    </Container>
  );
}
